import { Box, Stack, Typography, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { FoodsIcon } from "@foodpilot/foods";
import type { PaymentPlan } from "src/api/client-api/payment/usePaymentPlans";
import type { CompanySubscription as Subscription } from "src/api/client-api/payment/useCurrentSubscription";
import type { Company } from "src/api/client-api/company/useGetOneOrganization.ts";

type CurrentSubscriptionSectionProps = {
    subscription: Subscription;
    resolvedPlan: PaymentPlan | null;
    formattedAmount: string;
    intervalLabel: string;
    company: Company | null;
    onOpenPortal: () => void;
    portalLoading: boolean;
    canModify?: boolean;
};

export const CurrentSubscriptionSection = ({
    subscription,
    resolvedPlan,
    formattedAmount,
    intervalLabel,
    company,
    onOpenPortal,
    portalLoading,
    canModify = true,
}: CurrentSubscriptionSectionProps) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();

    return (
        <Box
            sx={{
                backgroundColor: "white",
                border: "1px solid #E0DEDD",
                borderRadius: theme.spacing(2),
                display: "flex",
                flexDirection: "column",
                gap: theme.spacing(4),
                p: theme.spacing(4.5),
            }}
        >
            <Typography
                variant="h2"
                sx={{
                    fontSize: "24px",
                    fontWeight: 700,
                    fontFamily: "Montserrat",
                    color: "#393A36",
                }}
            >
                {t("companySettings.subscription.current_plan", "Current plan")}
            </Typography>

            <Box
                sx={{
                    border: "1px solid #E0DEDD",
                    borderRadius: theme.spacing(1),
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                        borderBottom: "1px solid #E0DEDD",
                        px: theme.spacing(4),
                        py: theme.spacing(3),
                    }}
                >
                    <Typography
                        variant="h3"
                        sx={{
                            fontSize: "20px",
                            fontWeight: 700,
                            fontFamily: "Montserrat",
                            color: "#393A36",
                        }}
                    >
                        {resolvedPlan?.name ?? t("companySettings.subscription.plan_unknown", "[Offer name]")}
                    </Typography>
                    {canModify && (
                        <Stack direction="row" gap={theme.spacing(1.5)}>
                            <Button
                                variant="outlined"
                                onClick={onOpenPortal}
                                disabled={portalLoading}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                    fontSize: "14px",
                                    fontFamily: "Montserrat",
                                    borderColor: "#E7E6E4",
                                    color: "#393A36",
                                    background: "linear-gradient(180deg, #FFFFFF 0%, #F6F5F3 100%)",
                                    minWidth: "80px",
                                    height: "40px",
                                    borderRadius: theme.spacing(1),
                                    "&:hover": {
                                        borderColor: "#E7E6E4",
                                        background: "linear-gradient(180deg, #F6F5F3 0%, #EFEDEC 100%)",
                                    },
                                }}
                            >
                                {t("companySettings.subscription.unsubscribe", "Unsubscribe")}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={onOpenPortal}
                                disabled={portalLoading}
                                endIcon={<FoodsIcon icon="openNewTab" size={1.5} />}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                    fontSize: "14px",
                                    fontFamily: "Montserrat",
                                    color: "#FFFFFF",
                                    height: "40px",
                                    borderRadius: theme.spacing(1),
                                    boxShadow: "0px 4px 8px 0px rgba(42, 43, 39, 0.08)",
                                }}
                            >
                                {t("companySettings.subscription.switch_plan", "Switch plan")}
                            </Button>
                        </Stack>
                    )}
                </Stack>

                {/* Amount with badge */}
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={theme.spacing(1)}
                    sx={{ px: theme.spacing(4), pt: theme.spacing(3), pb: theme.spacing(1.5) }}
                >
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: "16px",
                            fontFamily: "Montserrat",
                            color: "#393A36",
                        }}
                    >
                        {t("companySettings.subscription.amount", "Amount")}:
                    </Typography>
                    <Typography
                        sx={{
                            fontWeight: 500,
                            fontSize: "14px",
                            fontFamily: "Montserrat",
                            color: "#393A36",
                        }}
                    >
                        {formattedAmount} {t("companySettings.subscription.tax_excluded_short", "excl. tax")} /{" "}
                        {intervalLabel}
                    </Typography>
                </Stack>

                {/* Subscription date */}
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={theme.spacing(1)}
                    sx={{ px: theme.spacing(4), py: theme.spacing(1.5) }}
                >
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: "16px",
                            fontFamily: "Montserrat",
                            color: "#393A36",
                        }}
                    >
                        {t("companySettings.subscription.subscription_date", "Subscription date")}:
                    </Typography>
                    <Typography
                        sx={{
                            fontWeight: 500,
                            fontSize: "14px",
                            fontFamily: "Montserrat",
                            color: "#393A36",
                        }}
                    >
                        {new Date(subscription.currentPeriodStart).toLocaleDateString(i18n.language, {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                        })}
                    </Typography>
                </Stack>

                {/* Renewal date (only shown if subscription is not scheduled to cancel) */}
                {!subscription.cancelAtPeriodEnd && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={theme.spacing(1)}
                        sx={{ px: theme.spacing(4), pt: theme.spacing(1.5), pb: theme.spacing(3) }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: "16px",
                                fontFamily: "Montserrat",
                                color: "#393A36",
                            }}
                        >
                            {t("companySettings.subscription.renewal_date", "Renewal date")}:
                        </Typography>
                        <Typography
                            sx={{
                                fontWeight: 500,
                                fontSize: "14px",
                                fontFamily: "Montserrat",
                                color: "#393A36",
                            }}
                        >
                            {new Date(subscription.currentPeriodEnd).toLocaleDateString(i18n.language, {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                            })}
                        </Typography>
                    </Stack>
                )}

                {/* End date (shown when subscription is scheduled to cancel) */}
                {subscription.cancelAtPeriodEnd && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={theme.spacing(1)}
                        sx={{ px: theme.spacing(4), pt: theme.spacing(1.5), pb: theme.spacing(3) }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: "16px",
                                fontFamily: "Montserrat",
                                color: "#393A36",
                            }}
                        >
                            {t("companySettings.subscription.end_date", "End date")}:
                        </Typography>
                        <Typography
                            sx={{
                                fontWeight: 500,
                                fontSize: "14px",
                                fontFamily: "Montserrat",
                                color: "#393A36",
                            }}
                        >
                            {new Date(subscription.cancelAtPeriodEnd).toLocaleDateString(i18n.language, {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                            })}
                        </Typography>
                    </Stack>
                )}
            </Box>
        </Box>
    );
};
